import { AbstractDate } from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import { Metric } from "@linkfluence/models/metric";
import { Card, DateTermCard } from "..";
import { Clause } from "@radarly/models/clause";
import {
  Dataviz,
  DatavizName,
  getDefaultDataviz,
} from "@radarly/models/dataviz";
import { SearchFragmentDateTerm } from "@radarly/models/search";
import { isDateTermCardDataviz } from "../dataviz";
import { ComparisonPeriod } from "../period";
import { DateTermCardTerm } from "../term";
import { CardFactory, CardFragmentsFactory } from "./base";
import { ICardFactory } from "./model";
import { TimeUnit } from "./time-unit";

interface DateTermCardFactoryOptions {
  term?: DateTermCardTerm;
  from?: AbstractDate;
  metric?: Metric;
  to?: AbstractDate;
  clause?: Clause;
  comparisonPeriod?: ComparisonPeriod;
  timeUnit?: TimeUnit;
}

export class DateTermCardFragmentsFactory extends CardFragmentsFactory {
  static create(input?: DateTermCardFactoryOptions) {
    const factory = new DateTermCardFragmentsFactory();
    return factory.getFragments(input);
  }
  static createFromCard(card: Card, input?: DateTermCardFactoryOptions) {
    const factory = new DateTermCardFragmentsFactory();
    const { from, to } = factory.getPeriod(card);
    const timeUnit = factory.getTimeUnitFromInput(input);
    return factory.getFragments({
      clause: factory.getClauseFromCard(card),
      from,
      to,
      term: factory.getTermFromCard(card, input) as DateTermCardTerm,
      metric: factory.getMetricFromCardAndInput(card, input),
      comparisonPeriod: factory.getComparaisonPeriod(card, null, input),
      timeUnit,
    });
  }
  private getFragments(input?: DateTermCardFactoryOptions) {
    return this.getComparaisonFragments(
      this.getFragment(input),
      input?.comparisonPeriod
    );
  }
  private getFragment(
    input?: DateTermCardFactoryOptions
  ): SearchFragmentDateTerm<[Metric]> {
    const term = input?.term || Facet.Platforms;
    const metric = input?.metric || Metric.Doc;
    const timeUnit = input?.timeUnit || "default";
    return {
      request: {
        query: this.getQuery(input),
        aggs: {
          metrics: { metrics: [metric] },
          date: {
            metrics: [metric],
            interval:
              timeUnit === "default" ? this.getInterval(input) : timeUnit,
            // @ts-ignore
            aggs: {
              [term]:
                term !== Facet.Focuses
                  ? {
                      metrics: [metric],
                      sort: {
                        on: metric,
                        order: "desc",
                      },
                      size: 100,
                    }
                  : {
                      metrics: [metric],
                    },
            },
          },
        },
      },
    };
  }
}

export class DateTermCardFactory
  implements ICardFactory<DateTermCard, DateTermCardFactoryOptions>
{
  public static getInstance() {
    return new DateTermCardFactory();
  }
  public fromCard(
    workspaceId: number,
    projectFocuses: number[],
    card: Card,
    input?: DateTermCardFactoryOptions & {
      dataviz?: DateTermCard["dataviz"];
    }
  ) {
    return CardFactory.getInstance<DateTermCard>().createCard(
      workspaceId,
      projectFocuses,
      "date-term",
      card.title,
      DateTermCardFragmentsFactory.createFromCard(card, input),
      this.createDataviz(input?.dataviz || card.dataviz)
    );
  }
  public fromInput(
    workspaceId: number,
    projectFocuses: number[],
    title: string,
    input?: DateTermCardFactoryOptions & {
      dataviz?: Dataviz["name"];
      fullDataviz?: Dataviz;
    }
  ) {
    return CardFactory.getInstance<DateTermCard>().createCard(
      workspaceId,
      projectFocuses,
      "date-term",
      title,
      DateTermCardFragmentsFactory.create(input),
      this.createDataviz(
        input?.fullDataviz ??
          getDefaultDataviz(input?.dataviz || DatavizName.HorizontalBarChart)
      )
    );
  }
  public createDataviz(dataviz: Dataviz) {
    if (isDateTermCardDataviz(dataviz)) {
      return dataviz;
    } else {
      return getDefaultDataviz(
        DatavizName.MultipleLineChart
      ) as DateTermCard["dataviz"];
    }
  }
}
